'use strict';

(() => {
  if (customElements.get('oluvie-cart-drawer')) return;
  customElements.define('oluvie-cart-drawer', class extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;
      this.config = JSON.parse(this.querySelector('[data-or-drawer-config]').textContent);
      this.dialog = this.querySelector('[data-or-drawer-dialog]');
      this.abort = new AbortController();
      this.readGeneration = 0;
      this.motionGeneration = 0;
      this.closeGeneration = this.closeGeneration || 0;
      this.busy = false;
      this.cartTrusted = false;
      this.addEventListener('click', (event) => this.onClick(event), { signal: this.abort.signal });
      this.addEventListener('change', (event) => {
        const input = event.target.closest('[data-or-drawer-quantity]');
        if (!input || this.busy) return;
        const item = this.findItem(input.closest('[data-line-key]')?.dataset.lineKey);
        if (!item) return;
        const quantity = Number(input.value);
        if (!Number.isInteger(quantity) || quantity < 1) { input.value = item.quantity; return; }
        if (quantity !== item.quantity) this.change(item.key, quantity, 'input');
      }, { signal: this.abort.signal });
      this.dialog.addEventListener('cancel', (event) => { event.preventDefault(); this.close(); }, { signal: this.abort.signal });
      this.dialog.addEventListener('close', () => {
        if (!this.dialog.open) this.releasePage(true);
      }, { signal: this.abort.signal });
      document.addEventListener('oluvie:cart-added', (event) => this.onCartAdded(event), { signal: this.abort.signal });
      window.addEventListener('pagehide', () => {
        this.pageReturnPosition = this.pageScrollLock ? { x: this.pageScrollLock.x, y: this.pageScrollLock.y } : null;
        this.closeImmediately(false);
      }, { signal: this.abort.signal });
      window.addEventListener('pageshow', (event) => {
        const position = this.pageReturnPosition;
        this.pageReturnPosition = null;
        if (!event.persisted) return;
        this.closeImmediately(false);
        if (!position) return;
        const generation = this.motionGeneration;
        // History can restore the zero offset recorded while the body was fixed
        // after pageshow. Restore the released position after that native step.
        requestAnimationFrame(() => requestAnimationFrame(() => {
          if (!this.isConnected || this.pageScrollLock || this.dialog.open || generation !== this.motionGeneration) return;
          this.restoreScrollPosition(position);
        }));
      }, { signal: this.abort.signal });
    }

    disconnectedCallback() {
      this.abort?.abort();
      this.readController?.abort();
      this.pageReturnPosition = null;
      this.closeImmediately(false);
      this.initialized = false;
    }

    captureAddInteraction() {
      return { drawer: this, closeGeneration: this.closeGeneration };
    }

    onCartAdded(event) {
      const detail = event.detail || {};
      const request = detail.cartRequest || (Array.isArray(detail.cart?.items) && Number.isFinite(detail.cart.item_count) && Number.isFinite(detail.cart.total_price) ? Promise.resolve(detail.cart) : null);
      const interaction = detail.drawerInteraction;
      const message = this.config.strings.added;
      if (interaction && (interaction.drawer !== this || interaction.closeGeneration !== this.closeGeneration)) {
        if (this.mutating) { this.pendingMessage = message; this.refreshPending = true; return; }
        void this.refresh(message, request);
        return;
      }
      this.open(message, detail.trigger, request);
    }

    open(message = '', trigger, cartRequest) {
      if (document.querySelector('oluvie-cart-drawer') !== this) return;
      clearTimeout(this.closeTimer);
      const generation = ++this.motionGeneration;
      if (!this.dialog.open) {
        this.releasePage(false);
        this.trigger = trigger || document.activeElement;
        this.lockPage();
        try { this.dialog.showModal(); }
        catch (error) { this.releasePage(false); throw error; }
      }
      requestAnimationFrame(() => { if (generation === this.motionGeneration && this.dialog.open) this.dialog.classList.add('is-visible'); });
      this.querySelector('[data-or-drawer-close]').focus({ preventScroll: true });
      if (this.mutating) { this.pendingMessage = message; this.refreshPending = true; return; }
      this.refresh(message, cartRequest);
    }

    lockPage() {
      if (this.pageScrollLock) return;
      const root = document.documentElement;
      const body = document.body;
      const capture = (element, names) => names.map((name) => [name, element.style.getPropertyValue(name), element.style.getPropertyPriority(name)]);
      this.pageScrollLock = {
        x: window.scrollX, y: window.scrollY,
        root: capture(root, ['overflow', 'scroll-behavior']),
        body: capture(body, ['position', 'top', 'left', 'width', 'overflow', 'padding-right'])
      };
      const scrollbar = Math.max(0, window.innerWidth - root.clientWidth);
      const padding = parseFloat(window.getComputedStyle(body).paddingRight) || 0;
      root.style.setProperty('overflow', 'hidden');
      body.style.setProperty('position', 'fixed');
      body.style.setProperty('top', `${-this.pageScrollLock.y}px`);
      body.style.setProperty('left', `${-this.pageScrollLock.x}px`);
      body.style.setProperty('width', '100%');
      body.style.setProperty('overflow', 'hidden');
      if (scrollbar) body.style.setProperty('padding-right', `${padding + scrollbar}px`);
    }

    releasePage(restoreFocus) {
      const state = this.pageScrollLock;
      if (!state) return;
      this.pageScrollLock = null;
      const restore = (element, values) => {
        for (const [name, value, priority] of values) {
          if (value) element.style.setProperty(name, value, priority);
          else element.style.removeProperty(name);
        }
      };
      restore(document.body, state.body);
      restore(document.documentElement, state.root);
      this.restoreScrollPosition(state);
      if (restoreFocus && this.trigger?.isConnected) this.trigger.focus({ preventScroll: true });
    }

    restoreScrollPosition(position) {
      const root = document.documentElement;
      const value = root.style.getPropertyValue('scroll-behavior');
      const priority = root.style.getPropertyPriority('scroll-behavior');
      root.style.setProperty('scroll-behavior', 'auto', 'important');
      try { window.scrollTo(position.x, position.y); }
      finally {
        if (value) root.style.setProperty('scroll-behavior', value, priority);
        else root.style.removeProperty('scroll-behavior');
      }
    }

    finishClose(restoreFocus = true) {
      if (this.dialog?.open) this.dialog.close();
      this.releasePage(restoreFocus);
    }

    close() {
      if (!this.dialog.open) return;
      ++this.closeGeneration;
      const generation = ++this.motionGeneration;
      this.dialog.classList.remove('is-visible');
      clearTimeout(this.closeTimer);
      if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) this.finishClose();
      else this.closeTimer = setTimeout(() => {
        if (generation === this.motionGeneration && this.dialog.open) this.finishClose();
      }, 400);
    }

    closeImmediately(restoreFocus = true) {
      ++this.closeGeneration;
      ++this.motionGeneration;
      clearTimeout(this.closeTimer);
      this.dialog?.classList.remove('is-visible');
      this.finishClose(restoreFocus);
    }

    onClick(event) {
      if (event.target === this.dialog) { this.close(); return; }
      const button = event.target.closest('button');
      if (!button || !this.contains(button)) return;
      if (button.hasAttribute('data-or-drawer-close')) { this.close(); return; }
      if (this.busy) return;
      if (button.hasAttribute('data-or-drawer-retry')) { this.refresh(); return; }
      const item = this.findItem(button.closest('[data-line-key]')?.dataset.lineKey);
      if (!item) return;
      if (button.hasAttribute('data-or-drawer-remove')) this.change(item.key, 0, 'remove');
      if (button.dataset.orDrawerDelta) this.change(item.key, Math.max(0, item.quantity + Number(button.dataset.orDrawerDelta)), button.dataset.orDrawerDelta);
    }

    findItem(key) { return this.cart?.items.find((item) => item.key === key); }

    async request(url, body, controller = new AbortController()) {
      const timeout = setTimeout(() => controller.abort(), 12000);
      try {
        const response = await fetch(url, {
          method: body ? 'POST' : 'GET', credentials: 'same-origin', cache: 'no-store',
          headers: { Accept: 'application/json', ...(body ? { 'Content-Type': 'application/json' } : {}) },
          ...(body ? { body: JSON.stringify(body) } : {}), signal: controller.signal,
        });
        const result = await response.json().catch(() => { throw new Error(this.config.strings.error); });
        if (!response.ok) throw new Error(result.description || this.config.strings.error);
        if (!Array.isArray(result.items) || !Number.isFinite(result.item_count) || !Number.isFinite(result.total_price)) throw new Error(this.config.strings.error);
        return result;
      } finally { clearTimeout(timeout); }
    }

    async readCart() {
      this.readController?.abort();
      this.readController = new AbortController();
      return this.request(this.config.cartUrl, null, this.readController);
    }

    async refresh(message = '', cartRequest) {
      const generation = ++this.readGeneration;
      this.cartTrusted = false;
      this.setBusy(true);
      this.setStatus(this.config.strings.loading);
      this.querySelector('[data-or-drawer-retry]').hidden = true;
      try {
        const cart = await (cartRequest || this.readCart());
        if (generation !== this.readGeneration || !this.isConnected) return;
        if (!Array.isArray(cart?.items) || !Number.isFinite(cart.item_count) || !Number.isFinite(cart.total_price)) throw new Error(this.config.strings.error);
        this.cartTrusted = true;
        this.render(cart);
        this.publish(cart);
        this.setStatus(message);
      } catch (error) {
        if (generation !== this.readGeneration || !this.isConnected) return;
        this.setStatus(error.name === 'AbortError' || error instanceof TypeError ? this.config.strings.networkError : error.message, true);
        this.querySelector('[data-or-drawer-retry]').hidden = false;
      } finally { if (generation === this.readGeneration && this.isConnected) this.setBusy(false); }
    }

    async change(key, quantity, control) {
      if (this.busy || !this.cartTrusted || !this.findItem(key)) return;
      this.mutating = true;
      this.cartTrusted = false;
      this.setBusy(true);
      this.setStatus(this.config.strings.loading);
      let changedCart;
      try {
        changedCart = await this.request(this.config.changeUrl, { id: key, quantity });
        const cart = await this.readCart();
        this.cartTrusted = true;
        this.render(cart);
        this.publish(cart);
        this.setStatus(this.config.strings.updated);
        this.restoreLineFocus(key, control);
      } catch (error) {
        const recovered = changedCart || await this.readCart().catch(() => null);
        if (recovered) { this.cartTrusted = true; this.render(recovered); this.publish(recovered); this.restoreLineFocus(key, control); }
        this.setStatus(error.name === 'AbortError' || error instanceof TypeError ? this.config.strings.networkError : error.message, true);
        this.querySelector('[data-or-drawer-retry]').hidden = false;
      } finally {
        this.mutating = false;
        this.setBusy(false);
        if (this.refreshPending) { this.refreshPending = false; this.refresh(this.pendingMessage); }
      }
    }

    restoreLineFocus(key, control) {
      if (!this.dialog.open) return;
      const line = Array.from(this.querySelectorAll('[data-line-key]')).find((node) => node.dataset.lineKey === key);
      const selector = control === 'input' ? '[data-or-drawer-quantity]' : control === 'remove' ? '[data-or-drawer-remove]' : `[data-or-drawer-delta="${control}"]`;
      const target = line?.querySelector(selector) || this.querySelector('[data-or-drawer-items] button') || this.querySelector('[data-or-drawer-close]');
      target.disabled = false;
      target.focus({ preventScroll: true });
    }

    money(cents) {
      const currency = this.cart?.currency || this.config.currency;
      if (currency === 'EUR' && /^nl(?:-|$)/i.test(this.config.locale)) {
        const amount = new Intl.NumberFormat(this.config.locale, { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(Number(cents) / 100);
        return `€ ${amount}`;
      }
      return new Intl.NumberFormat(this.config.locale, { style: 'currency', currency: this.cart?.currency || this.config.currency }).format(Number(cents) / 100);
    }

    reduction(original, final) {
      return Number.isSafeInteger(original) && Number.isSafeInteger(final) && original > final && final >= 0 ? original - final : 0;
    }

    appliedTier(cart) {
      if (!Number.isSafeInteger(cart?.item_count) || cart.item_count < 0) return 0;
      const titles = new Map([
        ['2 artikelen · 10% korting', 2],
        ['3 artikelen · 15% korting', 3],
        ['4 artikelen · 20% korting', 4],
        ['5+ artikelen · 25% korting', 5],
      ]);
      const allocations = (cart.items || []).flatMap((item) => item.line_level_discount_allocations || []);
      let tier = 0;
      for (const allocation of allocations) {
        const count = titles.get(allocation.discount_application?.title);
        if (Number.isSafeInteger(allocation.amount) && allocation.amount > 0 && count <= cart.item_count) tier = Math.max(tier, count);
      }
      for (const application of cart.cart_level_discount_applications || []) {
        const count = titles.get(application.title);
        if (Number.isSafeInteger(application.total_allocated_amount) && application.total_allocated_amount > 0 && count <= cart.item_count) tier = Math.max(tier, count);
      }
      return tier;
    }

    safeUrl(value) {
      try { const url = new URL(value, window.location.href); return ['http:', 'https:'].includes(url.protocol) ? url.href : ''; }
      catch (_) { return ''; }
    }

    thumbnailUrl(value) {
      const source = this.safeUrl(value);
      if (!source) return source;
      try {
        const url = new URL(source);
        const cdn = url.hostname === 'cdn.shopify.com' && /^\/s\/files\/.*\/(?:files|products)\//.test(url.pathname);
        const local = url.origin === window.location.origin && /^\/cdn\/shop\/(?:files|products)\//.test(url.pathname);
        if (!cdn && !local) return source;
        const width = Number(url.searchParams.get('width'));
        if (!Number.isFinite(width) || width <= 0 || width > 240) url.searchParams.set('width', '240');
        return url.href;
      } catch (_) { return source; }
    }

    render(cart) {
      this.cart = cart;
      const list = this.querySelector('[data-or-drawer-items]');
      const template = this.querySelector('[data-or-drawer-item]');
      const fragment = document.createDocumentFragment();
      for (const item of cart.items) {
        const node = template.content.cloneNode(true);
        node.querySelector('article').dataset.lineKey = item.key;
        const title = item.product_title || item.title;
        node.querySelector('[data-or-drawer-title]').textContent = title;
        for (const link of node.querySelectorAll('[data-or-drawer-product-link]')) { link.href = this.safeUrl(item.url); link.setAttribute('aria-label', title); link.title = title; }
        const image = node.querySelector('img');
        const source = this.thumbnailUrl(item.featured_image?.url || item.image || '');
        if (source && (item.featured_image?.url || item.image)) { image.src = source; image.alt = item.featured_image?.alt || title; }
        else image.hidden = true;
        const variant = node.querySelector('[data-or-drawer-variant]');
        variant.textContent = window.OluvieOptionLabel?.variantTitle(item.variant_title, item.product_has_only_default_variant) || (item.product_has_only_default_variant ? '' : item.variant_title || '');
        variant.hidden = !variant.textContent;
        const lineSaving = this.reduction(item.original_line_price, item.final_line_price);
        const original = node.querySelector('[data-or-drawer-original-price]');
        original.hidden = lineSaving === 0;
        original.textContent = lineSaving ? this.money(item.original_line_price) : '';
        original.setAttribute('aria-label', `${this.config.strings.originalPrice}: ${original.textContent}`);
        const final = node.querySelector('[data-or-drawer-price]');
        final.textContent = Number.isSafeInteger(item.final_line_price) && item.final_line_price >= 0 ? this.money(item.final_line_price) : '—';
        final.setAttribute('aria-label', `${this.config.strings.lineTotal}: ${final.textContent}`);
        const input = node.querySelector('[data-or-drawer-quantity]');
        input.value = item.quantity;
        input.setAttribute('aria-label', `${this.config.strings.quantity}: ${title}`);
        for (const button of node.querySelectorAll('[data-or-drawer-delta]')) button.setAttribute('aria-label', `${button.dataset.orDrawerDelta === '1' ? this.config.strings.increase : item.quantity === 1 ? this.config.strings.remove : this.config.strings.decrease}: ${title}`);
        node.querySelector('[data-or-drawer-remove]').setAttribute('aria-label', `${this.config.strings.remove}: ${title}`);
        fragment.append(node);
      }
      list.replaceChildren(fragment);
      this.querySelector('[data-or-drawer-count]').textContent = `(${cart.item_count})`;
      this.querySelector('[data-or-drawer-empty]').hidden = cart.item_count > 0;
      this.querySelector('[data-or-drawer-summary]').hidden = cart.item_count < 1;
      this.querySelector('[data-or-drawer-total]').textContent = this.money(cart.total_price);
      const tier = this.appliedTier(cart);
      const quantity = Number.isSafeInteger(cart.item_count) ? Math.max(0, cart.item_count) : 0;
      this.querySelector('[data-or-drawer-tiers]').style.setProperty('--or-tier-progress', `${Math.min(100, Math.max(0, (quantity - 2) / 3 * 100))}%`);
      for (const node of this.querySelectorAll('[data-or-drawer-tier]')) {
        const applied = tier > 0 && Number(node.dataset.orDrawerTier) === tier;
        node.classList.toggle('is-reached', quantity >= Number(node.dataset.orDrawerTier));
        node.classList.toggle('is-applied', applied);
        if (applied) node.setAttribute('aria-current', 'step');
        else node.removeAttribute('aria-current');
        node.querySelector('[data-or-tier-confirmed]').hidden = !applied;
      }
      const message = this.querySelector('[data-or-drawer-next-tier]');
      const next = [[2, 10], [3, 15], [4, 20], [5, 25]].find(([count]) => cart.item_count < count);
      const remaining = next ? next[0] - cart.item_count : 0;
      message.hidden = !next;
      message.textContent = next ? `Nog ${remaining} ${remaining === 1 ? 'artikel' : 'artikelen'} voor ${next[1]}% korting.` : '';
      const cartSaving = this.reduction(cart.original_total_price, cart.total_price);
      const originalTotal = this.querySelector('[data-or-drawer-original-total]');
      originalTotal.hidden = cartSaving === 0;
      originalTotal.textContent = cartSaving ? this.money(cart.original_total_price) : '';
      originalTotal.setAttribute('aria-label', `${this.config.strings.originalPrice}: ${originalTotal.textContent}`);
      const saving = this.querySelector('[data-or-drawer-saving]');
      saving.hidden = cartSaving === 0;
      saving.textContent = cartSaving ? `${this.config.strings.savings}: ${this.money(cartSaving)}` : '';
      this.querySelector('oluvie-cart-upsell')?.update?.(cart);
      this.setBusy(this.busy);
    }

    publish(cart) {
      for (const node of document.querySelectorAll('[data-or-cart-count], [data-or-mobile-cart-count]')) { node.textContent = String(cart.item_count); node.hidden = cart.item_count < 1; }
      for (const link of document.querySelectorAll('.or-header__cart')) link.setAttribute('aria-label', `${link.querySelector(':scope > span')?.textContent || this.querySelector('h2').textContent}, ${cart.item_count}`);
      document.dispatchEvent(new CustomEvent('oluvie:cart-updated', { detail: { cart, source: 'drawer' } }));
    }

    setBusy(busy) {
      this.busy = busy;
      this.dialog.setAttribute('aria-busy', String(busy));
      for (const control of this.querySelectorAll('[data-or-drawer-items] button, [data-or-drawer-items] input, [data-or-drawer-checkout]')) control.disabled = busy || !this.cartTrusted;
      this.querySelector('[data-or-drawer-retry]').disabled = busy;
      this.querySelector('oluvie-cart-upsell')?.setOwnerBusy?.(busy || !this.cartTrusted);
    }

    setStatus(message, error = false) {
      const status = this.querySelector('[data-or-drawer-status]');
      status.textContent = message;
      status.dataset.error = String(error);
      status.setAttribute('aria-live', error ? 'assertive' : 'polite');
    }
  });
})();
